import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { APIService, KPI } from '../api';

export type MainMenuItem = { key: string, label: string, icon: string, order: number }

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  // dependencies
  private api = inject(APIService);
  
  private readonly settings = toSignal(this.api.get_settings(), { initialValue: null });

  private getSetting(key: string): KPI.Setting | undefined {
    const setting = this.settings()?.find(s => s.name === key);
    if (!setting) {
      console.warn(`Setting with key "${key}" not found.`);
    }
    return setting
  }

  getSettingValue<T = any>(name: string, defaultValue?: T): T {
    const setting = this.getSetting(name);
    if (setting && setting.value !== undefined && setting.value !== null) {
      return setting.value as T;
    }
    return defaultValue as T;
  }

  /**
   * @deprecated: use getSettingValue instead
   */
  public getSettingValue$<T = any>(name: string, defaultValue?: T): Observable<T> {
    const setting = this.getSetting(name);
    if (setting && setting.value !== undefined && setting.value !== null) {
      return of(setting.value as T);
    }
    return of(defaultValue as T);
  }

  settingMustBeTrue(key: string): boolean {
    const setting = this.getSetting(key);
    return setting?.value === 'true' || !! setting?.value;
  }

  public getBooleanSetting(name: string, defaultValue: boolean = false): Observable<boolean> {
    return this.getSettingValue$<boolean>(name, defaultValue);
  }

  public getArraySetting<T = any>(name: string, defaultValue: T[] = []): Observable<T[]> {
    return this.getSettingValue$<T[]>(name, defaultValue);
  }

  public get assetTabs$(): Observable<string[]> {
    return this.getArraySetting<string>('ASSET__TABS', ['ASSET', 'ASSET_TYPE', 'SYNTHESIS']);
  }

  public get assetMaintenanceManagement$(): Observable<boolean> {
    return this.getBooleanSetting('ASSET__MAINTENANCE_MANAGEMENT', true);
  }

  public get assetConformityManagement$(): Observable<boolean> {
    return this.getBooleanSetting('ASSET__CONFORMITY_MANAGEMENT', true);
  }

  public get assetEquipmentValidation$(): Observable<boolean> {
    return this.getBooleanSetting('ASSET__EQUIPMENT_VALIDATION', true);
  }

  public get assetLifecycleMenu$(): Observable<boolean> {
    return this.getBooleanSetting('ASSET__LIFECYCLE_MENU', true);
  }

  // Energy-specific settings
  public get energyEnable$(): Observable<boolean> {
    return this.getBooleanSetting('ENERGY__ENABLE', true);
  }

  public get energyTabs$(): Observable<string[]> {
    return this.getArraySetting<string>('ENERGY__TABS', ['ACTION_TABLE', 'MEETING_REPORT', 'TRAJECTORIES', 'ENERGY_CARBON_TRAJECTORY', 'BOUQUET_TRAVAUX', 'FIDJI_ACT']);
  }

  public get energyTrajectoryTabs$(): Observable<string[]> {
    return this.getArraySetting<string>('ENERGY__TRAJECTORY_TABS', ['ACTION_TABLE', 'INVESTMENT_TABLE', 'PROJECT_TABLE']);
  }

  public get energyDefaultEnergyObjective$(): Observable<number> {
    return this.getSettingValue$<number>('ENERGY__DEFAULT_ENERGY_OBJECTIVE', 1);
  }

  public get energyDefaultCarbonObjective$(): Observable<number> {
    return this.getSettingValue$<number>('ENERGY__DEFAULT_CARBON_OBJECTIVE', 1);
  }

  public get systemMainMenu$(): Observable<MainMenuItem[]> {
    return this.getSettingValue$<MainMenuItem[]>(
      'SYSTEM__MAIN_MENU',
      []
    );
  }

  public get systemExportReconciliationAriba$(): Observable<boolean> {
    return this.getBooleanSetting('SYSTEM__EXPORT_RECONCILIATION_ARIBA', false);
  }

  public get workflowProjectCodeAtTheEndOfWF$(): Observable<boolean> {
    return this.getBooleanSetting('PROJECT_CODE_AT_THE_END_OF_WF', false);
  }

  public get auditAppSettings$(): Observable<string[]> {
    return this.getArraySetting<string>(
      'AUDIT_APP_SETTINGS',
      ['ACTIONS', 'DOCUMENTS', 'INVESTMENTS_TAB', 'INVESTMENTS_BY_EQUIPMENTS', 'PERIMETERS', 'REFERENCE_DATA']
    );
  }

  public get siteMapTab$(): Observable<boolean> {
    return this.getBooleanSetting('SITE__MAP_TAB', true);
  }

  public get siteControlPoints$(): Observable<boolean> {
    return this.getBooleanSetting('SITE__CONTROL_POINTS', true);
  }

  public get decisionFidjiActInMenu$(): Observable<boolean> {
    return this.energyTabs$.pipe(
      map(tabs => tabs.includes('FIDJI_ACT'))
      );
  }

  public get decisionSimplifiedActionTable$(): Observable<boolean> {
    return this.energyTabs$.pipe(
      map(tabs => tabs.includes('SIMPLIFIED_ACTION_TABLE'))
    );
  }

  public get decisionEnergyCarbonTrajectoryInMenu$(): Observable<boolean> {
    return combineLatest([
      this.energyEnable$,
      this.energyTabs$,
    ]).pipe(
      map(([energyEnabled, tabs]) => energyEnabled && tabs.includes('ENERGY_CARBON_TRAJECTORY'))
    );
  }

  public get decisionBouquetTravauxManagement$(): Observable<boolean> {
    return this.energyTabs$.pipe(
      map(tabs => tabs.includes('BOUQUET_TRAVAUX'))
    );
  }

  public get opexManagement$(): Observable<boolean> {
    return this.getBooleanSetting('OPEX_MANAGEMENT', true);
  }

  public get defectManagement$(): Observable<boolean> {
    return this.getBooleanSetting('SYSTEM__DEFECT_MANAGEMENT', false);
  }

}
